/*LIBRARY*/
import { useState } from "react";
import { connect } from "react-redux";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  LineElement,
  BarElement,
  PointElement,
  BarController,
  BubbleController,
  DoughnutController,
  LineController,
  PieController,
  PolarAreaController,
  RadarController,
  ScatterController,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,
  Decimation,
  Filler,
  Title,
  SubTitle,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import "chartjs-adapter-luxon";
import calculate_area from "@turf/area";

/*COMPONENT*/
import SINI_DOWNLOAD_UNIVERSAL from "./SINI_DOWNLOAD_UNIVERSAL";
import SINI_POLYGON_CONFIG from "./SINI_POLYGON_CONFIG";
import FLOAT_MENU from "../common_trivial/FLOAT_MENU";
import Modal from "../common_modal/Modal";

/*REDUX*/
import { set_value_sini } from "../../App/actions/sini_v2";

/*PICTURE*/
import ICON_BACK from "../../Assets/jsx/ICON_BACK";

/*FUNCTION*/
import sort_array from "../../App/validation/sort_array";

/*DATA*/
import dict from "../../Data/dict.json";
import manifest_demography from "../../Data/manifest_demography.json";
import twenty_colors from "../../Data/twenty_colors.json";

/*CONST*/
const animation_duration = 500;
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  BarController,
  BubbleController,
  DoughnutController,
  LineController,
  PieController,
  PolarAreaController,
  RadarController,
  ScatterController,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,
  Decimation,
  Filler,
  Title,
  SubTitle
);

const SINI_DEMOGRAPHY_FULL = ({ sini, set_value_sini, auth }) => {
  const [begin_at_zero, set_begin_at_zero] = useState(true);
  const [anchor, set_anchor] = useState(null);
  const [modal_download, set_modal_download] = useState(false);
  const [modal_layer_config, set_modal_layer_config] = useState(false);
  const [modal_detail_formula, set_modal_detail_formula] = useState(false);

  const toggle_detail_formula = () => {
    set_modal_detail_formula(!modal_detail_formula);
  };

  const toggle_layer_config = (e) => {
    set_modal_layer_config(!modal_layer_config);
    if (e) {
      set_anchor(e.currentTarget);
    }
  };

  const toggle_download = (e) => {
    set_modal_download(!modal_download);
    if (e) {
      set_anchor(e.currentTarget);
    }
  };

  const on_back = () => {
    set_value_sini({
      key: "sini_menu_active",
      value: "",
    });
    set_value_sini({
      key: "sini_map_show",
      value: ["poi", "isochrone"],
    });
  };

  const toggle_zoom_chart = () => {
    set_begin_at_zero(!begin_at_zero);
  };

  // local storage
  const language = localStorage?.language || auth.language;

  // global props
  const { request_id_active, ai_parent_detail_list, is_show_download_button } =
    sini;

  //content
  const sini_object = ai_parent_detail_list.find(
    (item) => item.request_id === request_id_active
  );
  const demography_properties = sini_object?.demography_properties || {};
  const demography_intersect_list =
    sini_object?.demography_intersect_list || [];
  const geojson_polygon_isochrone = sini_object?.geojson_polygon_isochrone;
  const isochrone_area = calculate_area(geojson_polygon_isochrone);

  const modal_layer_config_content = (
    <FLOAT_MENU
      id="basic-menu"
      anchorEl={anchor}
      open={modal_layer_config}
      onClose={toggle_layer_config}
    >
      <div style={{ width: "250px" }}>
        <SINI_POLYGON_CONFIG />
      </div>
    </FLOAT_MENU>
  );

  const modal_download_content = (
    <FLOAT_MENU
      id="basic-menu"
      anchorEl={anchor}
      open={modal_download}
      onClose={toggle_download}
    >
      <div style={{ width: "250px" }}>
        <SINI_DOWNLOAD_UNIVERSAL download_topic="demography_properties" />
      </div>
    </FLOAT_MENU>
  );

  const modal_detail_formula_content = modal_detail_formula && (
    <Modal
      modalSize="large"
      isOpen={modal_detail_formula}
      onClose={toggle_detail_formula}
    >
      <div className="box-body" id="box-body">
        {manifest_demography.map((item, idx) => {
          const { child, chart_type } = item;
          const totalPercent = demography_intersect_list.reduce(
            (sum, feature) => {
              const percent =
                (feature?.properties?.area_result_m2 / isochrone_area) * 100;
              return sum + percent;
            },
            0
          );
          const columnTotals = child.map((field) => {
            return demography_intersect_list.reduce((sum, feature) => {
              const value = parseFloat(feature?.properties?.[field?.key]) || 0;
              return sum + value;
            }, 0);
          });
          const total_all_number = columnTotals.reduce(
            (sum, total) => sum + total,
            0
          );
          return (
            <div key={idx} className="margin_bottom_extra">
              <p className="badge background_black margin_bottom">
                {item[language]}
              </p>
              <div style={{ overflowX: "auto" }}>
                <table className="table margin_top margin_bottom">
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>{dict?.["Village (Desa)"]?.[language]}</th>
                      <th>{dict?.["Percentage area"]?.[language]}</th>
                      {child.map((field, idx_field) => {
                        return <th key={idx_field}>{field?.key}</th>;
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {demography_intersect_list.map((feature, idx) => {
                      const percent = (
                        (feature?.properties?.area_result_m2 / isochrone_area) *
                        100
                      ).toFixed(4);

                      return (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{feature?.properties?.DESA}</td>
                          <td>
                            {new Intl.NumberFormat("id-ID", {
                              style: "decimal",
                              minimumFractionDigits: 4,
                              maximumFractionDigits: 4,
                            }).format(parseFloat(percent))}{" "}
                            %
                          </td>
                          {child.map((field, idx_field) => {
                            const value =
                              parseFloat(feature?.properties?.[field?.key]) ||
                              0;
                            return (
                              <td key={idx_field}>
                                {new Intl.NumberFormat("id-ID", {
                                  style: "decimal",
                                }).format(value)}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                    <tr>
                      <td>
                        <strong>Total</strong>
                      </td>
                      <td>
                        {chart_type === "pie" && (
                          <strong>
                            {new Intl.NumberFormat("id-ID", {
                              style: "decimal",
                            }).format(total_all_number)}
                          </strong>
                        )}
                      </td>
                      <td>
                        {new Intl.NumberFormat("id-ID", {
                          style: "decimal",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(totalPercent)}{" "}
                        %
                      </td>
                      {columnTotals.map((total, idx_total) => {
                        return (
                          <td key={idx_total}>
                            <strong>
                              {new Intl.NumberFormat("id-ID", {
                                style: "decimal",
                              }).format(total)}
                            </strong>
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );

  return (
    <main className="sini_full_parent">
      {modal_download_content}
      {modal_layer_config_content}
      {modal_detail_formula_content}
      <section className="sini_full_header">
        <table>
          <tbody>
            <tr>
              <td
                style={{ width: "30px" }}
                onClick={on_back}
                data-mapid="clickBack"
              >
                <ICON_BACK />
              </td>
              <td className="text_left">
                <p className="badge background_grey_light margin_bottom margin_right">
                  {dict?.["Demographics"]?.[language]}
                </p>
              </td>
              {is_show_download_button && (
                <td>
                  <button
                    className="badge background_grey_light margin_bottom"
                    onClick={toggle_download}
                  >
                    {dict?.["Download"]?.[language]}
                  </button>
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </section>

      <section className="sini_full_scroll_area">
        <div className="container_light background_white margin_bottom outline_grey">
          <p className="badge background_grey_light">
            {dict?.["Your SINI analysis is located at"]?.[language]}
          </p>
          <section className="margin_bottom">
            <p className="text_small">{dict?.["Village (Desa)"]?.[language]}</p>
            <p className="text_bold">
              {demography_properties?.["DESA"] ||
                demography_properties?.["DESA ATAU KELURAHAN"] ||
                ""}
            </p>
          </section>
          <section className="margin_bottom">
            <p className="text_small">
              {dict?.["Regency (Kabupaten)"]?.[language]}
            </p>
            <p className="text_bold">
              {demography_properties?.["KABKOT"] ||
                demography_properties?.["KABUPATEN ATAU KOTA"] ||
                ""}
            </p>
          </section>
        </div>

        <div className="container_light background_white margin_bottom outline_grey">
          <p className="badge background_grey_light">
            {dict?.["Villages in your SINI analysis area"]?.[language]}
          </p>
          <table className="table full_width margin_top">
            <thead>
              <tr>
                <th>No</th>
                <th>{dict?.["Village (Desa)"]?.[language]}</th>
                <th>{dict?.["Percentage area"]?.[language]}</th>
              </tr>
            </thead>
            <tbody>
              {demography_intersect_list.map((feature, idx) => {
                const percent = (
                  (feature?.properties?.area_result_m2 / isochrone_area) *
                  100
                ).toFixed(2);
                return (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>{feature?.properties?.DESA}</td>
                    <td>{percent} %</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <button
            className="button_small background_black margin_top"
            onClick={toggle_detail_formula}
          >
            Detail
          </button>
        </div>

        {manifest_demography.map((item, idx) => {
          const { chart_type, child } = item;
          let chart_content;
          if (chart_type === "line" || chart_type === "bar") {
            const label_array_line = child.map((item) => item[language]);
            const number_array_line = child.map((item) => {
              let number = Number(demography_properties?.[item?.key]);
              if (isNaN(number)) number = 0;
              return number;
            });
            const data = {
              labels: label_array_line,
              datasets: [
                {
                  label: child[language],
                  data: number_array_line,
                  fill: false,
                  backgroundColor: "#21a5f6",
                  borderColor: "#000",
                  hoverBackgroundColor: "#1a649d",
                  hoverBorderColor: "#d1f962",
                },
              ],
            };
            const options_line = {
              scales: {
                y: {
                  beginAtZero: begin_at_zero,
                },
              },
              plugins: {
                legend: {
                  display: false,
                },
              },
              animation: {
                duration: animation_duration,
              },
            };
            chart_content = (
              <div>
                <Chart type={chart_type} data={data} options={options_line} />
                {chart_type === "line" && (
                  <button
                    className="button_small background_grey"
                    onClick={toggle_zoom_chart}
                  >
                    {begin_at_zero
                      ? dict?.["Zoom slope"]?.[language]
                      : dict?.["View axis 0"]?.[language]}
                  </button>
                )}
              </div>
            );
          } else if (chart_type === "pie") {
            let array_of_object_pie = child.map((item) => {
              let number = Number(demography_properties?.[item?.key]);
              if (isNaN(number)) number = 0;
              return {
                label: item[language],
                number,
              };
            });
            array_of_object_pie = sort_array(
              array_of_object_pie,
              "number",
              false
            );
            const label_array_pie = array_of_object_pie.map(
              (item) => item.label
            );
            const number_array_pie = array_of_object_pie.map(
              (item) => item.number
            );
            const color_array = number_array_pie.map((item, idx) => {
              const index = idx % twenty_colors.length; // Safely map to st_colors

              return twenty_colors[index];
            });
            const data = {
              labels: label_array_pie,
              datasets: [
                {
                  label: item[language],
                  data: number_array_pie,
                  fill: false,
                  backgroundColor: color_array,
                  borderColor: "transparent",
                },
              ],
            };
            const options_pie = {
              plugins: {
                legend: {
                  display: false,
                },
              },
              animation: {
                duration: animation_duration,
              },
            };
            chart_content = (
              <main style={{ display: "flex", width: "100%" }}>
                <section style={{ width: "85px", flexShrink: 0 }}>
                  <Chart type="pie" data={data} options={options_pie} />
                </section>
                <section style={{ flexGrow: 1 }}>
                  <table className="full_width text_small top_aligned_cell hover_grey">
                    <tbody>
                      {array_of_object_pie.map(
                        (chart_element, index_table_chart) => {
                          return (
                            <tr key={index_table_chart}>
                              <td>{index_table_chart + 1}</td>
                              <td>
                                <div
                                  style={{
                                    width: "20px",
                                    height: "20px",
                                    borderRadius: "5px",
                                    backgroundColor:
                                      color_array[index_table_chart],
                                  }}
                                />
                              </td>
                              <td>{chart_element.label}</td>
                              <td>{chart_element.number}</td>
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </section>
              </main>
            );
          } else {
            chart_content = (
              <div>
                {child.map((child_item, index_child_item) => {
                  let value;
                  if (child_item.type === "interger") {
                    value = parseInt(demography_properties?.[child_item?.key]);
                  } else if (child_item.type === "float") {
                    const decimal =
                      child_item.decimal !== undefined ? child_item.decimal : 2;
                    value = parseFloat(
                      demography_properties?.[child_item?.key]
                    ).toFixed(decimal);
                  } else {
                    value =
                      demography_properties?.[child_item?.key] ||
                      demography_properties?.[child_item?.key_2];
                  }
                  return (
                    <div key={index_child_item} className="margin_bottom_extra">
                      <p className="text_small">{child_item[language]}</p>
                      <p className="text_bold">{value}</p>
                    </div>
                  );
                })}
              </div>
            );
          }
          return (
            <div key={idx} className="margin_bottom_extra">
              <p className="button_big background_grey_light margin_bottom">
                {item[language]}
              </p>
              {chart_content}
            </div>
          );
        })}
      </section>
    </main>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  sini: state.sini,
  loading_reducer: state.loading_reducer,
});

export default connect(mapStateToProps, {
  set_value_sini,
})(SINI_DEMOGRAPHY_FULL);
